import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, RefreshCw, Trash2, Wifi } from 'lucide-react';
import { API_BASE_URL } from '../config/api';
import { useToast } from '../shared/Toast';
import ConfirmModal from '../shared/ConfirmModal';
import { LoadFailed, SwitchRow } from '../settings/instantSettings';
import { SheetField } from '../settings/sheet';
import {
  ChipTone, Column, DANGER_GHOST_BTN, DataTable, DetailPanel, EmptyState, GHOST_BTN, INPUT_CLS, KeyValue, KeyValueItem,
  ListPage, ListToolbar, PRIMARY_BTN, StatusChip,
} from '../shared/listPage';

/**
 * Στοιχεία σύνδεσης παρόχων POS σε επίπεδο ΠΛΑΤΦΟΡΜΑΣ (migration 269, απόφαση ιδιοκτήτη 12/10/2026):
 * ένα σετ ανά πάροχο για όλα τα καταστήματα αυτού του server — «τα κλειδιά τα βγάζουμε εμείς». Η Viva
 * μένει ανά κατάστημα (δικός του merchant) και ορίζεται στις ρυθμίσεις POS του καταστήματος.
 * Ο κωδικός δεν έρχεται ποτέ από τον server· κενό πεδίο κωδικού = μένει ο αποθηκευμένος.
 *
 * Διάταξη «Πίνακας» (15/10/2026): ένας πάροχος ανά γραμμή (όνομα, κωδικός, εμβέλεια, χρήστης, καταστήματα,
 * κατάσταση)· κλικ στη γραμμή → συρτάρι με την κατάσταση, τη δοκιμή σύνδεσης και τη φόρμα, η Διαγραφή κάτω
 * αριστερά. Ως τότε κάρτες με εικονίδιο, κυανά κουμπιά και παράθυρο. Ίδια endpoints, ίδια σώματα, ίδιοι
 * έλεγχοι και ίδια μηνύματα με πριν — μόνο η δομή άλλαξε.
 */

export interface PosProviderCredentialsRow {
  provider_id: number;
  provider_code: string;
  provider_name: string;
  credentials_scope: 'platform' | 'store';
  platform: {
    username: string;
    is_active: boolean;
    has_password: boolean;
    created_at?: string;
    updated_at?: string;
  } | null;
  store_rows: number;
  store_sets: number;
  dependent_stores: number;
}

type ApiResult = { ok: boolean; status: number; data: any };

async function request(method: string, path: string, body?: unknown): Promise<ApiResult> {
  const headers: Record<string, string> = { Authorization: `Bearer ${localStorage.getItem('xentrix_token')}` };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = await response.json().catch(() => ({}));
  return { ok: response.ok, status: response.status, data };
}

interface ConfirmState {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning';
  onConfirm: () => void;
}
const CLOSED: ConfirmState = { open: false, title: '', message: '', onConfirm: () => {} };

interface CredentialsForm {
  username: string;
  password: string;
  is_active: boolean;
}
const EMPTY_FORM: CredentialsForm = { username: '', password: '', is_active: true };

// Ο κωδικός δεν προσυμπληρώνεται ποτέ· νέα στοιχεία ξεκινούν ενεργά.
const formFor = (row: PosProviderCredentialsRow): CredentialsForm => ({
  username: row.platform?.username || '',
  password: '',
  is_active: row.platform ? row.platform.is_active : true,
});

const FORM_ID = 'sa-pos-provider-form';
const isViva = (code?: string) => (code || '').toLowerCase() === 'viva';
const platformScoped = (row: PosProviderCredentialsRow) => row.credentials_scope === 'platform';

// Ώρα Ελλάδας και 24ωρη, όπως οι άλλες σελίδες του super admin (ο server γράφει UTC).
const formatDate = (value: string | undefined, language: string | undefined): string => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleString(language === 'en' ? 'en-GB' : 'el-GR', {
      timeZone: 'Europe/Athens', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    });
};

const SuperAdminPosProviders: React.FC = () => {
  const { t, i18n } = useTranslation(['settings', 'common', 'superAdminPanel', 'listPage']);
  const { showToast } = useToast();
  const [rows, setRows] = useState<PosProviderCredentialsRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [testing, setTesting] = useState<number | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState>(CLOSED);

  // Ο πάροχος στο συρτάρι (null = κλειστό), η φόρμα του και η αρχική της τιμή (για τις αλλαγές που θα χάνονταν).
  const [editing, setEditing] = useState<PosProviderCredentialsRow | null>(null);
  const [form, setForm] = useState<CredentialsForm>(EMPTY_FORM);
  const [initialForm, setInitialForm] = useState<CredentialsForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await request('GET', '/api/pos/provider-credentials');
      if (res.ok && Array.isArray(res.data)) {
        setRows(res.data);
        setLoadFailed(false);
      } else {
        setLoadFailed(true);
      }
    } catch (error) {
      console.error('Failed to load POS provider credentials:', error);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const errorText = (res: ApiResult) => res.data?.message || res.data?.error || `Error ${res.status}`;

  const scopeLabel = useCallback((row: PosProviderCredentialsRow) => (platformScoped(row)
    ? t('settings:pos.platform.scopePlatform')
    : t('settings:pos.platform.scopeStore')), [t]);

  // Κατάσταση των στοιχείων της πλατφόρμας (ίδια χρώματα με τις κάρτες ως 15/10)· η Viva δεν έχει.
  const stateChip = useCallback((row: PosProviderCredentialsRow): React.ReactNode => {
    if (!platformScoped(row)) return null;
    const [label, tone]: [string, ChipTone] = !row.platform
      ? [t('settings:pos.platform.stateMissing'), 'muted']
      : row.platform.is_active
        ? [t('settings:pos.platform.stateActive'), 'ok']
        : [t('settings:pos.platform.stateInactive'), 'warn'];
    return <StatusChip tone={tone}>{label}</StatusChip>;
  }, [t]);

  // Τι σημαίνει για τα καταστήματα: χρεώνουν με αυτά, ή — χωρίς ενεργά στοιχεία — δεν μπορούν να χρεώσουν
  // κάρτα (πορτοκαλί, το μόνο που θέλει προσοχή). Για τη Viva: σε πόσα καταστήματα έχουν οριστεί.
  // null = τίποτα να πει (κανένα κατάστημα δεν χρεώνει μόνο με της πλατφόρμας).
  const storesNote = useCallback((row: PosProviderCredentialsRow): React.ReactNode => {
    if (!platformScoped(row)) {
      return <span className="text-t2">{t('settings:pos.platform.storeScopeCount', { count: row.store_rows })}</span>;
    }
    if (row.dependent_stores <= 0) return null;
    return row.platform?.is_active
      ? <span className="text-t2">{t('settings:pos.platform.inUse', { count: row.dependent_stores })}</span>
      : (
        <span className="font-medium text-amber-800 dark:text-amber-300">
          {t('settings:pos.platform.noCredentialsStores', { count: row.dependent_stores })}
        </span>
      );
  }, [t]);

  const columns = useMemo<Column<PosProviderCredentialsRow>[]>(() => {
    const none = <span className="text-t4">—</span>;
    return [
      {
        key: 'provider',
        header: t('settings:pos.provider'),
        width: 'minmax(150px, 1.3fr)',
        render: r => <span className="font-medium">{r.provider_name}</span>,
        stackRender: r => r.provider_name,
      },
      {
        key: 'code',
        header: t('settings:pos.platform.colCode'),
        width: 'minmax(120px, 1fr)',
        hideBelow: 'medium',
        render: r => <span className="font-mono text-xs text-t3 break-all">{r.provider_code}</span>,
      },
      {
        key: 'scope',
        header: t('settings:pos.platform.colScope'),
        width: '140px',
        render: r => <span className="text-t2">{scopeLabel(r)}</span>,
        stackRender: r => scopeLabel(r),
      },
      {
        key: 'username',
        header: t('settings:pos.username'),
        width: 'minmax(140px, 1.2fr)',
        hideBelow: 'medium',
        render: r => (r.platform?.username ? <span className="text-t2 break-all">{r.platform.username}</span> : none),
      },
      {
        key: 'stores',
        header: t('settings:pos.platform.colStores'),
        width: 'minmax(200px, 2fr)',
        render: r => storesNote(r) ?? none,
        stackRender: r => storesNote(r),
      },
      {
        key: 'state',
        header: t('settings:pos.platform.colState'),
        width: '150px',
        // Στο κινητό η κατάσταση δεξιά από το όνομα, όπως το «Ενεργό» των Καταστημάτων (η Viva: «—»,
        // όπως στον πίνακα — όχι κενό κελί με μόνο την κρυφή ετικέτα).
        stack: 'end',
        render: r => stateChip(r) ?? none,
      },
    ];
  }, [t, scopeLabel, storesNote, stateChip]);

  const openForm = (row: PosProviderCredentialsRow) => {
    const next = formFor(row);
    setEditing(row);
    setForm(next);
    setInitialForm(next);
    setFormError(null);
  };

  const closeForm = () => {
    setEditing(null);
    setConfirm(CLOSED);
    setFormError(null);
  };

  const dirty = editing !== null && (form.username !== initialForm.username
    || form.password !== initialForm.password || form.is_active !== initialForm.is_active);

  // Esc / φόντο / X / Ακύρωση: πρώτα κλείνει μια ανοιχτή ερώτηση· με αλλαγές που δεν αποθηκεύτηκαν, ρωτά —
  // το παλιό παράθυρο δεν έκλεινε με Esc ή κλικ έξω, άρα ένας κωδικός που γράφτηκε δεν χανόταν κατά λάθος.
  const requestClose = () => {
    if (saving) return;
    if (confirm.open) { setConfirm(CLOSED); return; }
    if (dirty) {
      setConfirm({
        open: true,
        variant: 'warning',
        title: t('settings:pos.platform.discardTitle'),
        message: t('settings:pos.platform.discardMessage'),
        confirmLabel: t('settings:pos.platform.discardConfirm'),
        cancelLabel: t('settings:pos.platform.discardKeep'),
        onConfirm: closeForm,
      });
      return;
    }
    closeForm();
  };

  const change = <K extends keyof CredentialsForm>(key: K, value: CredentialsForm[K]) => {
    setFormError(null);
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const save = async (force = false) => {
    if (!editing || saving) return;
    if (!form.username.trim() || (!form.password && !editing.platform?.has_password)) {
      setFormError(t('settings:pos.credentialsRequired'));
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const res = await request('PUT', `/api/pos/provider-credentials/${editing.provider_id}${force ? '?force=1' : ''}`, {
        username: form.username.trim(),
        password: form.password,
        is_active: form.is_active,
      });
      if (res.ok) {
        showToast('success', t('settings:pos.saveSuccess'));
        closeForm();
        await load();
      } else if (res.status === 409 && res.data?.code === 'POS_CREDENTIALS_IN_USE' && !force) {
        // Απενεργοποίηση ενώ καταστήματα χρεώνουν μόνο με αυτά: ρωτάμε, και μόνο τότε force.
        setConfirm({
          open: true,
          variant: 'warning',
          title: t('settings:pos.platform.deactivateTitle', { provider: editing.provider_name }),
          message: t('settings:pos.platform.inUseWarning', { count: Number(res.data.stores) || 0 }),
          confirmLabel: t('settings:pos.platform.continue'),
          onConfirm: () => { setConfirm(CLOSED); save(true); },
        });
      } else {
        setFormError(errorText(res));
      }
    } catch (error) {
      console.error('Failed to save POS provider credentials:', error);
      setFormError(t('settings:pos.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row: PosProviderCredentialsRow, force: boolean) => {
    try {
      const res = await request('DELETE', `/api/pos/provider-credentials/${row.provider_id}${force ? '?force=1' : ''}`);
      if (res.ok) {
        showToast('success', t('settings:pos.platform.deleted'));
        // Τα στοιχεία δεν υπάρχουν πια: το συρτάρι τους κλείνει (αν δείχνει ακόμα αυτόν τον πάροχο).
        setEditing(prev => (prev && prev.provider_id === row.provider_id ? null : prev));
        await load();
      } else if (res.status === 409 && res.data?.code === 'POS_CREDENTIALS_IN_USE' && !force) {
        // Η λίστα ήταν παλιά: τώρα χρεώνουν καταστήματα — ξαναρωτάμε με το πλήθος του server.
        askDelete(row, Number(res.data.stores) || 0);
      } else {
        showToast('error', errorText(res));
      }
    } catch (error) {
      console.error('Failed to delete POS provider credentials:', error);
      showToast('error', t('settings:pos.saveError'));
    }
  };

  // inUse: καταστήματα που σταματούν να χρεώνουν (0 = χωρίς προειδοποίηση).
  const askDelete = (row: PosProviderCredentialsRow, inUse: number) => {
    setConfirm({
      open: true,
      variant: 'danger',
      title: t('settings:pos.platform.deleteTitle', { provider: row.provider_name }),
      message: [
        t('settings:pos.platform.deleteMessage', { provider: row.provider_name }),
        inUse > 0 ? t('settings:pos.platform.inUseWarning', { count: inUse }) : '',
      ].filter(Boolean).join(' '),
      confirmLabel: t('settings:pos.platform.delete'),
      // Με προειδοποίηση στην οθόνη → force· αλλιώς ο server ξαναρωτά αν άλλαξε κάτι στο μεταξύ.
      onConfirm: () => { setConfirm(CLOSED); remove(row, inUse > 0); },
    });
  };

  // Η δοκιμή χτυπά τα ΑΠΟΘΗΚΕΥΜΕΝΑ στοιχεία της πλατφόρμας (όχι ό,τι γράφεται στη φόρμα από κάτω).
  const test = async (row: PosProviderCredentialsRow) => {
    setTesting(row.provider_id);
    try {
      const res = await request('POST', `/api/pos/provider-credentials/${row.provider_id}/test`);
      if (res.ok && res.data?.success) {
        showToast('success', t('settings:pos.connectionSuccess'));
      } else {
        showToast('error', t('settings:pos.connectionError', { message: res.data?.message || t('settings:pos.unknownError') }));
      }
    } catch (error) {
      console.error('Failed to test POS provider credentials:', error);
      showToast('error', t('settings:pos.testConnectionError'));
    } finally {
      setTesting(null);
    }
  };

  // Η εικόνα του παρόχου στο συρτάρι — ό,τι έδειχνε ως 15/10 η κάρτα του, σε μορφή ετικέτα → τιμή.
  const statusItems = (row: PosProviderCredentialsRow): KeyValueItem[] => {
    const stores = storesNote(row);
    if (!platformScoped(row)) return [{ id: 'stores', label: t('settings:pos.platform.colStores'), value: stores }];
    const legacy = row.store_rows > 0
      ? t('settings:pos.platform.legacyRows', { count: row.store_rows, sets: row.store_sets })
      : null;
    const items: KeyValueItem[] = [{
      id: 'state',
      label: t('settings:pos.platform.colState'),
      value: stateChip(row),
      hint: row.platform?.updated_at
        ? t('settings:pos.platform.updated', { date: formatDate(row.platform.updated_at, i18n?.language) })
        : undefined,
    }];
    if (stores || legacy) {
      items.push({ id: 'stores', label: t('settings:pos.platform.colStores'), value: stores ?? legacy, hint: stores ? legacy : undefined });
    }
    return items;
  };

  const isPlatform = editing !== null && platformScoped(editing);

  const confirmModal = (
    <ConfirmModal
      isOpen={confirm.open}
      title={confirm.title}
      message={confirm.message}
      confirmLabel={confirm.confirmLabel}
      cancelLabel={confirm.cancelLabel}
      variant={confirm.variant}
      onConfirm={confirm.onConfirm}
      onCancel={() => setConfirm(CLOSED)}
    />
  );

  return (
    <ListPage
      eyebrow={t('superAdminPanel:nav.groups.connections')}
      title={t('settings:pos.platform.title')}
      subtitle={t('settings:pos.platform.subtitle')}
      testId="sa-pos-providers"
    >
      <ListToolbar
        actions={(
          <button type="button" className={GHOST_BTN} onClick={() => load()} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
            {t('settings:pos.platform.refresh')}
          </button>
        )}
        summary={!loadFailed && rows.length > 0 ? t('settings:pos.platform.count', { count: rows.length }) : null}
      />

      {loadFailed ? (
        <LoadFailed message={t('settings:pos.platform.loadError')} onRetry={() => load()} />
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={r => r.provider_id}
          onRowClick={openForm}
          selectedKey={editing?.provider_id ?? null}
          loading={loading}
          stickyHeader
          skeletonRows={4}
          empty={<EmptyState title={t('settings:pos.platform.empty')} />}
          testId="sa-pos-providers-table"
        />
      )}

      <DetailPanel
        open={editing !== null}
        onClose={requestClose}
        title={editing ? t('settings:pos.credentialsTitleFor', { provider: editing.provider_name }) : ''}
        subtitle={editing ? `${editing.provider_code} · ${scopeLabel(editing)}` : undefined}
        footerStart={isPlatform && editing?.platform ? (
          <button
            type="button"
            className={DANGER_GHOST_BTN}
            onClick={() => askDelete(editing, editing.platform?.is_active ? editing.dependent_stores : 0)}
            disabled={saving}
            data-testid="sa-pos-provider-delete"
          >
            <Trash2 className="w-4 h-4" aria-hidden="true" />
            {t('settings:pos.platform.delete')}
          </button>
        ) : undefined}
        footer={isPlatform ? (
          <>
            <button type="button" className={GHOST_BTN} onClick={requestClose} disabled={saving} data-testid="sa-pos-provider-cancel">
              {t('common:cancel')}
            </button>
            <button type="submit" form={FORM_ID} className={PRIMARY_BTN} disabled={saving} aria-busy={saving || undefined}>
              {saving && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
              {t('common:save')}
            </button>
          </>
        ) : (
          <button type="button" className={GHOST_BTN} onClick={requestClose}>{t('listPage:close')}</button>
        )}
        testId="sa-pos-provider-panel"
      >
        {editing && (isPlatform ? (
          <div className="space-y-5">
            <KeyValue items={statusItems(editing)} testId="sa-pos-provider-status" />
            {editing.platform && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <button
                  type="button"
                  className={GHOST_BTN}
                  onClick={() => test(editing)}
                  disabled={testing === editing.provider_id}
                  aria-busy={testing === editing.provider_id || undefined}
                >
                  {testing === editing.provider_id
                    ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                    : <Wifi className="w-4 h-4" aria-hidden="true" />}
                  {t('settings:pos.testConnection')}
                </button>
                <span className="text-xs text-t3">{t('settings:pos.platform.testHint')}</span>
              </div>
            )}

            <form
              id={FORM_ID}
              onSubmit={e => { e.preventDefault(); save(false); }}
              noValidate
              className="space-y-5 pt-5 border-t border-line"
            >
              <p className="text-sm text-t3">{t('settings:pos.platform.formHint')}</p>
              <div className="xs-grid2">
                <SheetField
                  label={isViva(editing.provider_code) ? t('settings:pos.clientId') : t('settings:pos.username')}
                  htmlFor="pos-platform-username"
                >
                  <input
                    id="pos-platform-username"
                    value={form.username}
                    onChange={e => change('username', e.target.value)}
                    placeholder={t('settings:pos.usernamePlaceholder')}
                    autoComplete="one-time-code"
                    spellCheck={false}
                    className={INPUT_CLS}
                  />
                </SheetField>
                <SheetField
                  label={isViva(editing.provider_code) ? t('settings:pos.clientSecret') : t('settings:pos.password')}
                  htmlFor="pos-platform-password"
                >
                  <input
                    id="pos-platform-password"
                    type="password"
                    value={form.password}
                    onChange={e => change('password', e.target.value)}
                    placeholder={editing.platform?.has_password ? t('settings:pos.secretKeepPlaceholder') : t('settings:pos.passwordPlaceholder')}
                    autoComplete="one-time-code"
                    className={INPUT_CLS}
                  />
                </SheetField>
              </div>
              <SwitchRow
                title={t('settings:pos.platform.stateActive')}
                subtitle={t('settings:pos.platform.activeHint')}
                checked={form.is_active}
                onChange={v => change('is_active', v)}
              />
              {formError && <p role="alert" className="text-sm font-medium text-red-600 dark:text-red-400">{formError}</p>}
            </form>
          </div>
        ) : (
          // Ανά κατάστημα (Viva): τίποτα να οριστεί εδώ — μόνο πού ορίζεται και σε πόσα καταστήματα.
          <div className="space-y-5">
            <p className="text-sm text-t2">{t('settings:pos.platform.storeScopeNote')}</p>
            <KeyValue items={statusItems(editing)} testId="sa-pos-provider-status" />
          </div>
        ))}
        {/* Έξω από τη φόρμα: τα κουμπιά του ConfirmModal δεν έχουν type και θα την υπέβαλλαν. */}
        {confirmModal}
      </DetailPanel>
    </ListPage>
  );
};

export default SuperAdminPosProviders;
